import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { captureReferralFromUrl, getStoredReferral, clearStoredReferral, getVisitorId } from '@/lib/referral';

/**
 * Captures `?ref=CODE` on any page and, once the user has a session
 * (email signup or Google), registers the referral server-side.
 */
export function useReferralAttribution() {
  const location = useLocation();
  const { user } = useAuth();
  const claimedFor = useRef<string | null>(null);
  const clickedCodes = useRef<Set<string>>(new Set());

  useEffect(() => {
    const code = captureReferralFromUrl(location.search);
    if (!code) return;
    if (clickedCodes.current.has(code)) return;
    clickedCodes.current.add(code);

    (async () => {
      try {
        await (supabase as any).rpc('register_referral_click', {
          _code: code,
          _visitor_hash: getVisitorId(),
          _path: location.pathname,
        });
      } catch {
        /* click tracking is best-effort */
      }
    })();
  }, [location.search, location.pathname]);


  useEffect(() => {
    if (!user?.id) return;
    const code = getStoredReferral();
    if (!code) return;
    if (claimedFor.current === user.id) return;
    claimedFor.current = user.id;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('register-referral', {
          body: { code },
        });
        if (error) {
          console.error('register-referral error', error);
          return;
        }
        const reason = (data as any)?.reason;
        // Stop retrying on terminal outcomes.
        if (
          (data as any)?.ok ||
          ['invalid_code', 'self_referral', 'already_referred', 'disabled'].includes(reason)
        ) {
          clearStoredReferral();
        }
      } catch (e) {
        console.error('register-referral failed', e);
      }
    })();

  }, [user?.id]);
}
